import type { ToolDefinition } from "@arcadeai/arcadejs/resources/index.mjs";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { OAUTH2_PROVIDER } from "@arcadeai/design-system/metadata/oauth-providers";
import { findPrebuiltProviderByProviderId } from "@arcadeai/design-system/metadata/utils";
import { Link } from "@tanstack/react-router";
import { KeyRound } from "lucide-react";

type Authorization = ToolDefinition.Requirements.Authorization;
type Secret = ToolDefinition.Requirements.Secret;
type ToolRequirements = ToolDefinition.Requirements;

const AuthorizationBadge = ({
  authorization,
}: {
  authorization: Authorization;
}) => {
  const authIdentifier = authorization.id ?? authorization.provider_id;
  if (!authIdentifier) {
    return null;
  }

  const descriptor =
    findPrebuiltProviderByProviderId(authIdentifier) ?? OAUTH2_PROVIDER;

  return (
    <Link
      from="/orgs/$organizationId/projects/$projectId/auth/oauth"
      search={{ name: authIdentifier }}
      to="."
    >
      <Badge className="flex items-center gap-2" variant="secondary">
        <descriptor.icon className="size-3" />
        {authIdentifier}
      </Badge>
    </Link>
  );
};

const SecretBadge = ({ secret }: { secret: Secret }) => (
  <Link
    from="/orgs/$organizationId/projects/$projectId/auth/secrets"
    search={{ name: secret.key }}
    to="."
  >
    <Badge className="flex items-center gap-2" variant="secondary">
      <KeyRound className="size-3" />
      {secret.key}
    </Badge>
  </Link>
);

export const RequirementsCell = ({
  requirements,
}: {
  requirements: ToolRequirements | undefined;
}) => {
  if (!requirements) {
    return <Badge variant="outline">None</Badge>;
  }

  const { authorization, secrets } = requirements;

  // If neither authorization nor secrets are present
  if (!(authorization || secrets)) {
    return <Badge variant="outline">None</Badge>;
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {authorization ? (
        <AuthorizationBadge authorization={authorization} />
      ) : null}
      {secrets?.map((secret) => (
        <SecretBadge key={secret.key} secret={secret} />
      ))}
    </div>
  );
};
